import { c as _c } from "react/compiler-runtime";
import { alpha, Box, Stack, TableCell, TableRow, useTheme } from "@mui/material";
import React, { useEffect, useMemo, useRef } from "react";
import { TableVirtuoso, type TableVirtuosoHandle } from "react-virtuoso";
import { type CallFrame, selectCallstack } from "#/features/callstack/model/callstackSlice";
import { useI18nContext } from "#/shared/hooks";
import { useMobileLayout } from "#/shared/hooks/useMobileLayout";
import { useAppSelector } from "#/store/hooks";
import { CallstackArgumentsValue, CallstackNodeBadge } from "./CallstackFrameContent";
const BOTTOM_SPACER_HEIGHT = "calc(38px + env(safe-area-inset-bottom, 0px))";

/** Sentinel for the spacer row appended on mobile so last item can scroll above nav bar */
const SPACER_SENTINEL = Symbol("callstack-bottom-spacer");
type TableRowData = CallFrame | typeof SPACER_SENTINEL;
function isSpacerRow(frame: TableRowData): frame is typeof SPACER_SENTINEL {
  return frame === SPACER_SENTINEL;
}
export const CallstackTable: React.FC = () => {
  const $ = _c(22);
  const {
    LL
  } = useI18nContext();
  const theme = useTheme();
  const isMobile = useMobileLayout();
  const callstack = useAppSelector(selectCallstack);
  const virtuosoRef = useRef(null);
  const startTimestamp = callstack.startTimestamp ?? 0;
  let t0;
  if ($[0] !== callstack.frames || $[1] !== isMobile) {
    t0 = isMobile ? [...callstack.frames, SPACER_SENTINEL] : callstack.frames;
    $[0] = callstack.frames;
    $[1] = isMobile;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const tableData = t0;
  let t1;
  if ($[3] !== callstack.frameIndex || $[4] !== callstack.frames.length || $[5] !== theme) {
    t1 = index => {
      if (index >= callstack.frames.length) {
        return "transparent";
      }
      let backgroundColor = "transparent";
      if (index === callstack.frameIndex) {
        backgroundColor = alpha(theme.palette.primary.light, 0.1);
      } else {
        if (index < callstack.frameIndex) {
          backgroundColor = alpha(theme.palette.primary.light, 0.032);
        }
      }
      return backgroundColor;
    };
    $[3] = callstack.frameIndex;
    $[4] = callstack.frames.length;
    $[5] = theme;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const getBackgroundColor = t1;
  let t2;
  let t3;
  if ($[7] !== callstack) {
    t2 = () => {
      const {
        frameIndex,
        frames
      } = callstack;
      if (frameIndex >= 0 && frameIndex < frames.length && virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex({
          index: frameIndex,
          align: "center",
          behavior: "smooth"
        });
      }
    };
    t3 = [callstack, callstack.frameIndex, callstack.frames.length];
    $[7] = callstack;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  if (!callstack.isReady) {
    let t4;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Box sx={{
        p: 2,
        textAlign: "center"
      }}>Here you will see a table of runtime actions once the code is executed!</Box>;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      height: "100%",
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
      overflow: "hidden",
      table: {
        width: "100%",
        borderCollapse: "collapse"
      },
      tr: {
        position: "relative"
      }
    };
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      height: "100%",
      width: "100%"
    };
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== LL) {
    t6 = () => <TableRow sx={{
      backdropFilter: "blur(10px)"
    }}><TableCell component="th">{LL.NODE()}</TableCell><TableCell component="th" align="right">{LL.ACTION()}</TableCell><TableCell component="th" align="right">{LL.TIMESTAMP()}</TableCell><TableCell component="th" align="left">{LL.ARGUMENTS()}</TableCell></TableRow>;
    $[13] = LL;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== getBackgroundColor || $[16] !== startTimestamp) {
    t7 = (index_0, frame) => {
      if (isSpacerRow(frame)) {
        return <TableCell colSpan={4} sx={{
          padding: 0,
          border: "none"
        }}><Box sx={{
            height: BOTTOM_SPACER_HEIGHT
          }} aria-hidden={true} /></TableCell>;
      }
      return <><TableCell component="th" scope="row" sx={{
          "&::before": {
            content: "''",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: getBackgroundColor(index_0),
            pointerEvents: "none"
          }
        }}>{"treeName" in frame && "nodeId" in frame && <CallstackNodeBadge treeName={frame.treeName} id={frame.nodeId} />}</TableCell><TableCell align="right">{frame.name}</TableCell><TableCell align="right">{`+${(frame.timestamp - startTimestamp).toFixed(2)} ms`}</TableCell><TableCell align="left"><CallstackArgumentsValue frame={frame} /></TableCell></>;
    };
    $[15] = getBackgroundColor;
    $[16] = startTimestamp;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7 || $[20] !== tableData) {
    t8 = <Stack spacing={2} sx={t4}><TableVirtuoso ref={virtuosoRef} style={t5} data={tableData} fixedHeaderContent={t6} itemContent={t7} /></Stack>;
    $[18] = t6;
    $[19] = t7;
    $[20] = tableData;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};